import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { clearAllOrderWizardDrafts } from '@/utils/orderWizardDraft'

/**
 * Store Zustand per la gestione dell'autenticazione.
 * 
 * Gestisce:
 * - Token JWT (access e refresh)
 * - Dati utente corrente
 * - Stato di autenticazione
 * - Azioni di login/logout
 * 
 * I token vengono persistiti in localStorage per mantenere
 * la sessione tra i refresh della pagina.
 */
const useAuthStore = create(
  persist(
    (set, get) => ({
      // Stato
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      // Azioni
      
      /**
       * Imposta i token dopo il login.
       */
      setTokens: (accessToken, refreshToken) => {
        set({
          accessToken,
          refreshToken,
          isAuthenticated: true,
        })
        // Salva anche in localStorage per l'interceptor axios
        localStorage.setItem('access_token', accessToken)
        localStorage.setItem('refresh_token', refreshToken)
      },

      /**
       * Imposta i dati dell'utente corrente.
       */
      setUser: (user) => {
        set({ user })
      },

      /**
       * Aggiorna solo l'access token (dopo refresh).
       */
      updateAccessToken: (accessToken) => {
        set({ accessToken })
        localStorage.setItem('access_token', accessToken)
      },

      /**
       * Imposta lo stato di loading.
       */
      setLoading: (isLoading) => {
        set({ isLoading })
      },

      /**
       * Esegue il logout, pulendo tutti i dati.
       */
      logout: () => {
        clearAllOrderWizardDrafts()
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
      },

      /**
       * Verifica se l'utente ha un determinato permesso.
       */
      hasPermission: (permission) => {
        const user = get().user
        if (!user) return false
        
        switch (permission) {
          case 'manage_orders':
            return user.can_manage_orders
          case 'manage_inventory':
            return user.can_manage_inventory
          case 'manage_products':
            return user.can_manage_products
          case 'view_reports':
            return user.can_view_reports
          default:
            return false
        }
      },

      /**
       * Verifica se l'utente è admin.
       */
      isAdmin: () => {
        const user = get().user
        return user?.ruolo === 'admin'
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

export default useAuthStore
